import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckCircleOutline,
  CloudUpload,
  ContentCopy,
  DeleteOutline,
  PhoneIphone,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { amazonColors } from "../../../config/amazonTheme";
import {
  MANUAL_PAYMENT_CONFIG,
  ManualPaymentMethod,
} from "../../../config/paymentConfig";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { convertUsdToTjs } from "../../../services/exchangeRateService";
import { uploadPaymentScreenshot } from "../../../services/uploadService";
import NoTranslate from "../../common/NoTranslate";

const BRAND = {
  orange: amazonColors.orange || "#FF9900",
  blue: "#00A8E1",
  dark: "#111111",
};

const createManualPaymentSchema = (t: (key: string) => string) =>
  z.object({
    customerPhone: z
      .string()
      .min(1, t("checkout.phoneNumberRequired"))
      .regex(/^\+992\d{9}$/, t("checkout.phoneFormat")),
  });

type ManualPaymentFormData = z.infer<ReturnType<typeof createManualPaymentSchema>>;

interface ManualPaymentFormProps {
  paymentMethod: ManualPaymentMethod;
  amount: number;
  onSubmit: (data: {
    customerPhone: string;
    screenshotUrl?: string;
  }) => Promise<void>;
  onCancel?: () => void;
  onFormReady?: (submitFn: (() => Promise<void>) | null) => void;
  onSubmitStateChange?: (isSubmitting: boolean) => void;
  mobileSidebar?: React.ReactNode;
}

const ManualPaymentForm: React.FC<ManualPaymentFormProps> = ({
  paymentMethod,
  amount,
  onSubmit,
  onFormReady,
  onSubmitStateChange,
  mobileSidebar,
}) => {
  const { t: myTranslate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTimedOut, setUploadTimedOut] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const config = MANUAL_PAYMENT_CONFIG[paymentMethod];

  const { data: exchangeRate, isLoading: isLoadingRate } = useExchangeRate();
  const tjsAmount = useMemo(
    () => (exchangeRate ? convertUsdToTjs(amount, exchangeRate) : null),
    [amount, exchangeRate]
  );

  const manualPaymentSchema = useMemo(
    () => createManualPaymentSchema(myTranslate),
    [myTranslate]
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ManualPaymentFormData>({
    resolver: zodResolver(manualPaymentSchema),
    defaultValues: { customerPhone: "+992" },
  });

  useEffect(() => {
    onSubmitStateChange?.(isSubmitting || isUploading);
  }, [isSubmitting, isUploading, onSubmitStateChange]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleCopyPhone = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(config.phoneNumber);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [config.phoneNumber]);

  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let digits = event.target.value.replace(/\D/g, "");
      if (!digits.startsWith("992")) {
        digits = `992${digits}`;
      }
      digits = digits.slice(0, 12);
      setValue("customerPhone", `+${digits}`, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const attemptUpload = useCallback(async (file: File) => {
    setUploadError(null);
    setUploadTimedOut(false);
    setIsUploading(true);
    try {
      const url = await uploadPaymentScreenshot(file);
      setScreenshotUrl(url);
    } catch (error) {
      const isTimeout =
        (error instanceof Error && /timeout/i.test(error.message)) ||
        (error as { code?: string })?.code === "ECONNABORTED";
      if (isTimeout) {
        setUploadTimedOut(true);
        setUploadError(myTranslate("checkout.uploadTimeout"));
        // Keep file + preview so the user can retry without re-selecting
      } else {
        setUploadError(
          error instanceof Error
            ? error.message
            : myTranslate("checkout.uploadFailed")
        );
        setScreenshotFile(null);
        setScreenshotUrl(null);
        setPreviewUrl(null);
      }
    } finally {
      setIsUploading(false);
    }
  }, [myTranslate]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setUploadError(myTranslate("checkout.fileTypeError"));
        return;
      }

      const maxSize = 3 * 1024 * 1024;
      if (file.size > maxSize) {
        setUploadError(myTranslate("checkout.fileSizeError"));
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setScreenshotFile(file);
      await attemptUpload(file);
    },
    [attemptUpload, myTranslate]
  );

  const handleRemoveFile = useCallback(() => {
    setScreenshotFile(null);
    setScreenshotUrl(null);
    setPreviewUrl(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const onValidSubmit = useCallback(
    async (data: ManualPaymentFormData) => {
      if (!screenshotUrl) {
        setUploadError(myTranslate("checkout.screenshotRequired"));
        return;
      }
      try {
        await onSubmit({
          ...data,
          screenshotUrl,
        });
      } catch {
        // Error handling is done by parent component.
      }
    },
    [onSubmit, screenshotUrl, myTranslate]
  );

  useEffect(() => {
    if (!onFormReady) return undefined;
    const submitFn = async () => {
      await handleSubmit(onValidSubmit)();
    };
    onFormReady(submitFn);
    return () => onFormReady(null);
  }, [onFormReady, handleSubmit, onValidSubmit]);

  return (
    <Box
      className="notranslate"
      translate="no"
      sx={{
        width: "100%",
        maxWidth: 480,
        mx: "auto",
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ textAlign: "center", pt: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            sx={{ letterSpacing: "0.05em" }}
          >
            {myTranslate("checkout.amountToPay")}
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mb={0.5}
          >
            <Typography variant="h3" fontWeight={800} color={BRAND.dark}>
              <NoTranslate>${amount.toFixed(2)}</NoTranslate>
            </Typography>
            <Chip
              label="USD"
              size="small"
              sx={{
                bgcolor: BRAND.dark,
                color: "white",
                fontWeight: 700,
                height: 24,
              }}
            />
          </Box>

          {!isLoadingRate && tjsAmount !== null && (
            <Box
              display="inline-flex"
              alignItems="center"
              sx={{
                bgcolor: alpha(BRAND.blue, 0.1),
                color: BRAND.blue,
                px: 2,
                py: 0.5,
                borderRadius: 5,
                mt: 0.5,
              }}
            >
              <Typography variant="body1" fontWeight={700}>
                <NoTranslate>≈ {tjsAmount.toFixed(2)} c.</NoTranslate>
              </Typography>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {myTranslate("checkout.pleaseSendPaymentTo")}
            </Typography>

            <Tooltip title={copied ? myTranslate("checkout.copied") : myTranslate("checkout.tapToCopy")} placement="top">
              <Button
                fullWidth
                onClick={handleCopyPhone}
                sx={{
                  py: 2,
                  border: `2px dashed ${BRAND.orange}`,
                  borderRadius: 3,
                  bgcolor: alpha(BRAND.orange, 0.02),
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  textTransform: "none",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(BRAND.orange, 0.08),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color={BRAND.orange}
                  sx={{ letterSpacing: "0.08em", lineHeight: 1.2 }}
                >
                  <NoTranslate>{config.phoneNumber}</NoTranslate>
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  {copied ? (
                    <CheckCircleOutline fontSize="small" color="success" />
                  ) : (
                    <ContentCopy
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {copied ? myTranslate("checkout.copied") : myTranslate("checkout.tapToCopy")}
                  </Typography>
                </Stack>
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(BRAND.dark, 0.1)}`,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={3}>
            {myTranslate("checkout.paymentInstructions")}
          </Typography>

          <Stack spacing={3}>
            <TextField
              {...register("customerPhone")}
              onChange={handlePhoneChange}
              label={myTranslate("checkout.yourPhoneNumber")}
              placeholder="+992XXXXXXXXX"
              fullWidth
              required
              error={!!errors.customerPhone}
              helperText={
                errors.customerPhone?.message ||
                myTranslate("checkout.phoneNumberHelperText")
              }
              InputProps={{
                sx: { borderRadius: 2 },
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphone
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                {myTranslate("checkout.uploadScreenshot")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                {myTranslate("checkout.uploadScreenshotDesc")}
              </Typography>

              <Box
                component="input"
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileChange}
                aria-label={myTranslate("checkout.uploadScreenshot")}
                title={myTranslate("checkout.uploadScreenshot")}
                sx={{ display: "none" }}
              />

              {!previewUrl ? (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${alpha(BRAND.blue, 0.3)}`,
                    borderRadius: 2,
                    bgcolor: alpha(BRAND.blue, 0.02),
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: BRAND.blue,
                      bgcolor: alpha(BRAND.blue, 0.05),
                    },
                  }}
                >
                  {isUploading ? (
                    <CircularProgress size={24} sx={{ color: BRAND.blue }} />
                  ) : (
                    <>
                      <CloudUpload
                        sx={{ fontSize: 32, color: BRAND.blue, mb: 1 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={500}
                      >
                        {myTranslate("checkout.chooseScreenshot")}
                      </Typography>
                    </>
                  )}
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={screenshotFile?.name || "Screenshot"}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      objectFit: "cover",
                      bgcolor: "#f5f5f5",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {screenshotFile?.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <CheckCircleOutline
                        sx={{ fontSize: 14, color: "success.main" }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight={500}
                      >
                        {myTranslate("checkout.readyToSubmit")}
                      </Typography>
                    </Stack>
                  </Box>
                  <IconButton
                    onClick={handleRemoveFile}
                    color="error"
                    size="small"
                    sx={{ flexShrink: 0 }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Paper>
              )}

              {uploadError && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 1.5,
                    borderRadius: 2,
                    fontSize: "0.875rem",
                    border: "1.5px solid",
                    borderColor: "error.main",
                  }}
                  action={
                    uploadTimedOut && screenshotFile ? (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={isUploading}
                        onClick={() => attemptUpload(screenshotFile)}
                        sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                      >
                        {isUploading
                          ? myTranslate("common.loading")
                          : myTranslate("common.retry")}
                      </Button>
                    ) : undefined
                  }
                >
                  {uploadError}
                </Alert>
              )}
            </Box>

            {!onFormReady && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit(onValidSubmit)}
                disabled={isSubmitting || isUploading}
                sx={{
                  bgcolor: BRAND.blue,
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 700,
                  "&:hover": { bgcolor: alpha(BRAND.blue, 0.9) },
                }}
              >
                {isSubmitting
                  ? myTranslate("checkout.processing")
                  : myTranslate("checkout.pay")}
              </Button>
            )}
          </Stack>
        </Paper>

        {isMobile && mobileSidebar && <Box sx={{ mt: 2 }}>{mobileSidebar}</Box>}
      </Stack>
    </Box>
  );
};

export default ManualPaymentForm;
